import React from 'react';
import { SimpleGrid, Container, useToast, Center, Box, Heading, useColorModeValue } from '@chakra-ui/react';
import { Api } from 'api';
import { Announcement, Customer, CustomerResponse } from 'types';
import { DashboardReportResponse } from 'types/dashboard-report.types';
import { customerResponseToCustomer } from 'core';
import { LoadingIndicator } from 'components';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { MonthlyTransactionsCard } from './dashboard/MonthlyTransactionsCard';
import { QueriesCard } from './dashboard/QueriesCard';
import { ReportSearchCard } from './dashboard/ReportSearchCard';
import { AccountingCard } from './dashboard/AccountingCard';
import { AnnouncementCard } from './dashboard/AnnouncementCard';
import { SalesCard } from './dashboard/SalesCard';

export const Dashboard = ({
  onSearchComplete,
  selectedGroupType,
}: {
  onSearchComplete: (customer: Customer) => void;
  selectedGroupType?: number | string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const bgGradient = useColorModeValue('linear(to-br, blue.50, gray.50)', 'linear(to-br, gray.900, blue.900)');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  const onSubmitSearch = (query: string, isReportNumber: boolean = false) => {
    const data = isReportNumber
      ? {
          customerId: parseInt(query, 10),
          brandId: '',
          modelId: '',
          vehicleTypeId: '',
        }
      : {
          plate: query,
          brandId: '',
          modelId: '',
          vehicleTypeId: '',
        };
    Api.Customer.searchCustomer2(data)
      .then((result) => {
        if (result.data.length) {
          const results = result.data;
          const formatCustomer: CustomerResponse = {
            customer: results[0],
            canContinue: results[0].canContinue,
            displayPlate: results[0].plate,
            hasKvkkApproval: results[0].hasKvkkApproval,
            hasIsemriApproval: results[0].hasIsemriApproval,
          };
          onSearchComplete(customerResponseToCustomer(formatCustomer));
          toast({
            status: 'info',
            title: t`Veri arama sonuçları listesine eklendi`,
            isClosable: true,
            duration: 3000,
            position: 'top-right',
            variant: 'solid',
          });
        } else {
          toast({
            status: 'warning',
            title: t`Sonuç bulunamadı`,
            isClosable: true,
            duration: 3000,
            position: 'top-right',
            variant: 'solid',
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleQuery = () => {
    navigate('/admin/inquiries');
  };

  const handleAddBalance = () => {
    navigate('/admin/balance');
  };

  const handleViewAllAnnouncements = () => {
    navigate('/admin/announcement');
  };

  const { data: dashboardReportData, isLoading: isDashboardReportLoading } = useQuery<DashboardReportResponse>(
    ['dashboardReport', selectedGroupType],
    async () => {
      const groupType = selectedGroupType === '' ? undefined : Number(selectedGroupType);
      const response = await Api.Reporting.getDashboardReport(groupType);
      return response.data;
    },
  );

  const { data: announcement } = useQuery<Announcement>(
    'announcements',
    async () => {
      const response = await Api.Util.getUserAnnouncements();
      return response.data[0];
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  const getCurrentMonthName = () => {
    const localeMap: Record<string, string> = {
      tr: 'tr-TR',
      ru: 'ru-RU',
      en: 'en-US',
    };
    const locale = localeMap[i18n.locale] || 'en-US';
    return new Date().toLocaleString(locale, { month: 'long' });
  };

  const transactionData = dashboardReportData?.currentMonthSales
    ? {
        today: {
          period: t`Bugün`,
          vehicleCount: dashboardReportData.currentMonthSales.today.count,
          amount: formatCurrency(dashboardReportData.currentMonthSales.today.amount),
        },
        lastSevenDays: {
          period: t`Son Yedi Gün`,
          vehicleCount: dashboardReportData.currentMonthSales.last7Days.count,
          amount: formatCurrency(dashboardReportData.currentMonthSales.last7Days.amount),
        },
        thisMonth: {
          period: t`Bu Ay`,
          vehicleCount: dashboardReportData.currentMonthSales.thisMonth.count,
          amount: formatCurrency(dashboardReportData.currentMonthSales.thisMonth.amount),
        },
      }
    : null;

  const queriesData = dashboardReportData?.currentMonthQueries
    ? {
        damageQueries: {
          type: t`Hasar Sorgu`,
          count: dashboardReportData.currentMonthQueries.damageQuery.count,
          amount: formatCurrency(dashboardReportData.currentMonthQueries.damageQuery.amount),
        },
        kmQueries: {
          type: t`KM Sorgu`,
          count: dashboardReportData.currentMonthQueries.kilometerQuery.count,
          amount: formatCurrency(dashboardReportData.currentMonthQueries.kilometerQuery.amount),
        },
        totalQueries: {
          type: t`Toplam Sorgu`,
          count: dashboardReportData.currentMonthQueries.totalOfQueries.count,
          amount: formatCurrency(dashboardReportData.currentMonthQueries.totalOfQueries.amount),
        },
      }
    : null;

  const accountingData = dashboardReportData?.financialSummary
    ? {
        balance: formatCurrency(dashboardReportData.financialSummary.currentBalance),
        currentMonth: {
          month: t`Bu Ay`,
          vehicleCount: dashboardReportData.financialSummary.currentMonthSales.count,
          amount: formatCurrency(dashboardReportData.financialSummary.currentMonthSales.amount),
        },
        previousMonth: {
          month: t`Geçen Ay`,
          vehicleCount: dashboardReportData.financialSummary.previousMonthSales.count,
          amount: formatCurrency(dashboardReportData.financialSummary.previousMonthSales.amount),
        },
      }
    : null;

  const salesSummary = dashboardReportData?.salesSummary
    ? {
        currentMonth: {
          vehicleCount: dashboardReportData.salesSummary.thisMonth.count,
          amount: formatCurrency(dashboardReportData.salesSummary.thisMonth.amount),
        },
        thisWeek: {
          vehicleCount: dashboardReportData.salesSummary.thisWeek.count,
          amount: formatCurrency(dashboardReportData.salesSummary.thisWeek.amount),
        },
        today: {
          vehicleCount: dashboardReportData.salesSummary.today.count,
          amount: formatCurrency(dashboardReportData.salesSummary.today.amount),
        },
      }
    : null;

  if (isDashboardReportLoading) {
    return (
      <Box bgGradient={bgGradient} minH="90vh" py={10}>
        <Container maxW="container.xl">
          <Center h="70vh">
            <LoadingIndicator />
          </Center>
        </Container>
      </Box>
    );
  }

  if (!dashboardReportData) {
    return (
      <Box bgGradient={bgGradient} minH="90vh" py={10}>
        <Container maxW="container.xl">
          <Center h="70vh">
            <LoadingIndicator />
          </Center>
        </Container>
      </Box>
    );
  }

  return (
    <Box bgGradient={bgGradient} minH="100vh" py={8} px={4}>
      <Container maxW="container.2xl">
        <Box mb={8}>
          <Heading size="lg" mb={2} textAlign="left">
            <Trans>Pano</Trans>
          </Heading>
          <Box w="60px" h="4px" bg="blue.500" borderRadius="md" mb={6} />
        </Box>

        <SimpleGrid columns={{ base: 1, md: 1, xl: 3 }} spacing={6} mb={6}>
          {salesSummary && (
            <Box
              bg={cardBg}
              p={5}
              borderRadius="lg"
              boxShadow="lg"
              transition="transform 0.3s, box-shadow 0.3s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <SalesCard salesSummary={salesSummary} />
            </Box>
          )}

          {transactionData && (
            <Box
              bg={cardBg}
              p={5}
              borderRadius="lg"
              boxShadow="lg"
              transition="transform 0.3s, box-shadow 0.3s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <MonthlyTransactionsCard
                title={t`${getCurrentMonthName()} Ayı İşlemleri`}
                today={transactionData.today}
                lastSevenDays={transactionData.lastSevenDays}
                thisMonth={transactionData.thisMonth}
                percentageChange={dashboardReportData.currentMonthSales.percentageRateFromLastMonth.toFixed(2)}
              />
            </Box>
          )}

          {queriesData && (
            <Box
              bg={cardBg}
              p={5}
              borderRadius="lg"
              boxShadow="lg"
              transition="transform 0.3s, box-shadow 0.3s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <QueriesCard
                title={t`${getCurrentMonthName()} Ayı Sorgulamaları`}
                damageQueries={queriesData.damageQueries}
                kmQueries={queriesData.kmQueries}
                totalQueries={queriesData.totalQueries}
                onQuery={handleQuery}
              />
            </Box>
          )}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 1, xl: 3 }} spacing={6} mb={6}>
          {accountingData && (
            <Box
              bg={cardBg}
              p={5}
              borderRadius="lg"
              boxShadow="lg"
              transition="transform 0.3s, box-shadow 0.3s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <AccountingCard
                balance={accountingData.balance}
                currentMonth={accountingData.currentMonth}
                previousMonth={accountingData.previousMonth}
                onAddBalance={handleAddBalance}
              />
            </Box>
          )}

          <Box
            bg={cardBg}
            p={5}
            borderRadius="lg"
            boxShadow="lg"
            transition="transform 0.3s, box-shadow 0.3s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <ReportSearchCard onSearch={onSubmitSearch} />
          </Box>
        </SimpleGrid>

        <Box
          bg={cardBg}
          p={5}
          borderRadius="lg"
          boxShadow="lg"
          transition="transform 0.3s, box-shadow 0.3s"
          _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <AnnouncementCard
            announcement={
              announcement ||
              ({
                id: 0,
                title: '',
                description: '',
                createdAt: new Date(),
              } as unknown as Announcement)
            }
            onViewAllAnnouncements={handleViewAllAnnouncements}
          />
        </Box>
      </Container>
    </Box>
  );
};
